import React, { useContext } from "react";
import { GlobalContext } from "../../context/provider";
import numberbeautify from "../../utils/numberbeautify";
import iqd_rounding from "../../utils/iqd_rounding";
import { FiAlertTriangle, FiArrowDownLeft, FiArrowUpRight, FiDollarSign, FiPackage, FiRefreshCw, FiShoppingBag, FiTool, FiTrendingDown, FiTrendingUp } from "react-icons/fi";

const Summarytable = () => {
  const { initState } = useContext(GlobalContext);
  const sales = initState?.summary_report_sell_array || [];
  const amount = value => Number(value) || 0;
  const money = value => `${numberbeautify(iqd_rounding(amount(value)))} IQD`;
  const sum = (items, field) => items.reduce((total, item) => total + amount(item?.[field]), 0);

  const itemProfit = sum(sales, "item_sell_price");
  const itemSales = sum(sales, "item_static_sell_price");
  const cashSales = sales.reduce((total, item) => (
    total + (item?.sell_invoice_type === "cash" ? amount(item?.item_base_static_sell_price) : 0)
  ), 0);
  const serviceSales = amount(initState?.summary_report_total_service_price);
  const serviceCash = amount(initState?.summary_report_total_service_price_cash);
  const companyReturns = amount(initState?.summary_report_return_item_company_amount);
  const loanIn = amount(initState?.loan_in) - amount(initState?.loan_in_discount);
  const expenses = amount(initState?.summary_report_expense_amount);
  const loanOut = amount(initState?.loan_out) - amount(initState?.loan_out_discount);
  const returnedSales = amount(initState?.summary_report_total_return_price);
  const totalIncome = cashSales + serviceCash + companyReturns + loanIn;
  const totalDeductions = expenses + loanOut + returnedSales;
  const net = totalIncome - totalDeductions;
  const netIsPositive = net >= 0;

  const Metric = ({ icon: Icon, label, value, tone = "slate", detail }) => (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tone === "green" ? "bg-emerald-50 text-emerald-600" : tone === "red" ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"}`}>
        <Icon size={19} />
      </div>
      <div className="min-w-0 flex-1 text-right">
        <p className="text-xs font-medium text-slate-400">{label}</p>
        <p className="mt-1 text-sm font-semibold text-slate-800">{value}</p>
        {detail && <p className="mt-1 text-xs text-slate-400">{detail}</p>}
      </div>
    </div>
  );

  const Section = ({ title, icon: Icon, children, tone }) => (
    <section className="rounded-xl border border-slate-100 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4 text-right">
        <div className={`rounded-lg p-2 ${tone === "green" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
          <Icon size={17} />
        </div>
        <h2 className="flex-1 text-sm font-semibold text-slate-700">{title}</h2>
      </div>
      <div className="divide-y divide-slate-50">{children}</div>
    </section>
  );

  const Line = ({ icon: Icon, label, value, detail, detail2 }) => (
    <div className="flex items-center justify-between gap-4 px-5 py-4 text-right">
      <div className="flex min-w-0 items-center gap-3">
        {Icon && <Icon className="shrink-0 text-slate-400" size={17} />}
        <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        {detail && <p className="mt-1 text-xs text-slate-400">{detail}</p>}
        </div>
      </div>
      <div>
        <p className="shrink-0 text-sm font-semibold text-slate-800">{value}</p>
        {detail2 && <p className="mt-1 text-xs text-slate-400 line-through">{detail2}</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" dir="rtl">
      <div className={`rounded-2xl p-6 text-white shadow-sm ${netIsPositive ? "bg-emerald-600" : "bg-rose-600"}`}>
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-white/75">پوختەی قاسە</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight">{money(net)}</h2>
            <p className="mt-2 text-sm text-white/75">داهات - خەرجییەکان</p>
          </div>
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15">
            {netIsPositive ? <FiTrendingUp size={27} /> : <FiTrendingDown size={27} />}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric icon={FiShoppingBag} label="کۆی فرۆشی کاڵا" value={money(itemSales)} tone="green" detail={`${numberbeautify(sales.length)} پسوڵە`} />
        <Metric icon={FiTool} label="کۆی خزمەتگوزاری" value={money(serviceSales)} tone="green" />
        <Metric icon={FiTrendingUp} label="کۆی قازانجی (کاڵا)" value={money(itemProfit)} tone="green" />
        <Metric icon={FiTrendingUp} label="کۆی قازانج (کاڵا + خزمەتگوزاری)" value={money(serviceSales+itemProfit)} tone="green" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="داهاتەکان" icon={FiTrendingUp} tone="green">
          <Line icon={FiRefreshCw} label="گەڕاندنەوە بۆ کۆمپانیا" value={money(companyReturns)} detail={'کاڵای گەڕاوە بۆ کۆمپانیا'} />
          <Line icon={FiArrowDownLeft} label="قەرزی وەرگیراو" value={money(loanIn)} detail={`داشکاندن: ${money(initState?.loan_in_discount)}`} detail2={money(initState?.loan_in)} />
        </Section>
        <Section title="کەمکردنەوەکان" icon={FiTrendingDown} tone="red">
          <Line icon={FiArrowUpRight} label="قەرزی دراو" value={money(loanOut)} detail={`داشکاندن: ${money(initState?.loan_out_discount)}`} detail2={Number(initState?.loan_out_discount)>0?money(initState?.loan_out):null}/>
          <Line icon={FiRefreshCw} label="کاڵای گەڕاوە" value={money(returnedSales)} detail={'کاڵای گەڕاوە لە لایەن کڕیارەوە'}/>
        </Section>
      </div>

      <section className="rounded-xl border border-slate-100 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4 text-right">
          <div className="rounded-lg bg-slate-100 p-2 text-slate-500"><FiPackage size={17} /></div>
          <h2 className="flex-1 text-sm font-semibold text-slate-700">زانیارییە پشتیوانەکان</h2>
        </div>
        <div className="grid grid-cols-1 divide-y divide-slate-50 sm:grid-cols-2 sm:divide-x sm:divide-y-0 sm:divide-x-reverse">
          <Metric icon={FiDollarSign} label="خەرجییەکان" value={money(expenses)} />
          <Metric icon={FiAlertTriangle} label="کاڵای خەسار" value={money(initState?.summary_report_damage_amount)} detail={`${numberbeautify(initState?.summary_report_damage_qty)} دانە`} />
        </div>
      </section>
    </div>
  )
}

export default Summarytable